'use client'

import { useSearchParams } from 'next/navigation'
import { SIZE_SEARCH_PARAM } from 'product/constants/constants'
import {
	type Dispatch,
	type SetStateAction,
	createContext,
	useContext,
	useMemo,
	useState,
} from 'react'

import type { Color, Size } from '../../types/FullProduct'
import {
	type GetSelectedInfoProps,
	getSelectedSize,
} from '../../utils/getSelectedInfo/getSelectedInfo'
import { useColors } from '../ColorsProvider/ColorsProvider'

export type SizesContextType = {
	isSheetOpen: boolean
	isUniqueSize: boolean
	openSheet: (isOpen: boolean) => void
	select: (size: Size | undefined) => void
	sizes: Size[]
	selectedSize?: Size
	hasErrorMessage: boolean
	setHasErrorMessage: Dispatch<SetStateAction<boolean>>
}

type SizesProviderProps = {
	children: React.ReactNode
	searchParams: Record<string, string>
}

export const SizesContext = createContext<SizesContextType | undefined>(
	undefined
)

export function SizesProvider({
	children,
	searchParams,
}: SizesProviderProps): React.ReactNode {
	const { selectedColor, colors } = useColors()
	const [isSheetOpen, setIsSheetOpen] = useState(false)
	const [hasErrorMessage, setHasErrorMessage] = useState(false)
	const defaultSize = getDefaultSize({
		sizeIdSearchParam: useSearchParams().get(SIZE_SEARCH_PARAM),
		searchParams,
		colors,
	})
	const [selectedSize, setSelectedSize] = useState(defaultSize)

	const isUniqueSize = useMemo(
		() => selectedColor.sizes.length === 1,
		[selectedColor]
	)

	const openSheet = (isOpen: boolean): void => {
		setIsSheetOpen(isOpen)
	}

	const value = useMemo(
		() => ({
			isSheetOpen,
			isUniqueSize,
			openSheet,
			selectedSize,
			hasErrorMessage,
			setHasErrorMessage,
			select: setSelectedSize,
			sizes: selectedColor.sizes,
		}),
		[isSheetOpen, isUniqueSize, selectedSize, selectedColor, hasErrorMessage]
	)

	return <SizesContext.Provider value={value}>{children}</SizesContext.Provider>
}

/** @throws {Error} If useSizes is used without SizesProvider */
export const useSizes = (): SizesContextType => {
	const context = useContext(SizesContext)
	if (!context) {
		throw new Error('useSizes must be used within a SizesProvider')
	}
	return context
}

type GetDefaultSizeProps = {
	colors: Color[]
	searchParams: Record<string, string>
	sizeIdSearchParam: string | null
}

/**
 * We have to get the default size from the client search params
 * because we are ignoring the server size param (s)
 * in order to do not trigger the lambda.
 */
const getDefaultSize = ({
	colors,
	searchParams,
	sizeIdSearchParam,
}: GetDefaultSizeProps): Size | undefined => {
	const newParams: GetSelectedInfoProps = {
		colors,
		searchParams,
	}
	if (sizeIdSearchParam) {
		newParams.searchParams[SIZE_SEARCH_PARAM] = sizeIdSearchParam
	}
	return getSelectedSize(newParams)
}
